import { Injectable, computed, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { OrganizationResponseDto, UpdateOrganizationDto } from "@libs/shares/dto/organization.dto";
import { BehaviorSubject, firstValueFrom, map, tap } from "rxjs";
import {
  OrganizationIndustryDto,
  OrganizationRoleDto,
  OrganizationSizeDto
} from "@libs/shares/dto/organization-enums.dto";
import { AuthHttpClient } from "../auth/auth-client.service";
import { BillingInfoDto } from "@libs/shares/dto/createPayment.dto";
import { Scopes } from "@libs/shares/models/auth.model";
import { GetDuneVolDto } from "@libs/shares/dto/duneVol.dto";

@Injectable({
  providedIn: "root"
})
export class ProfileService {
  organizations = signal<OrganizationResponseDto[] | undefined>(undefined);
  organization$ = new BehaviorSubject<OrganizationResponseDto[] | undefined>(undefined);
  currentOrganization = computed(() => this.organizations()?.[0]);

  constructor(private authHttpClient: AuthHttpClient) {}

  async refreshOrganizations() {
    const organizations = await firstValueFrom(
      this.authHttpClient
        .get<OrganizationResponseDto[]>(`${environment.apiBaseUrl}/organizations`)
        .pipe(map((organizations) => organizations))
    );

    this.organizations.set(organizations);
    this.organization$.next(organizations);
  }

  deleteProfile() {
    return this.authHttpClient.delete(`${environment.apiBaseUrl}/users/me`);
  }

  updateOrganization(
    value: Partial<{
      organizationName: string | null;
      firstName: string | null;
      lastName: string | null;
      organizationUrl: string | null;
      role: OrganizationRoleDto | null;
      organizationSize: OrganizationSizeDto | null;
      organizationIndustry: OrganizationIndustryDto | null;
    }>
  ) {
    // TODO: currently we assume that the user has only one organization -> if we support multiple organizations, we need to change this
    const organization = this.organizations()?.[0];
    return this.authHttpClient
      .patch<UpdateOrganizationDto>(`${environment.apiBaseUrl}/organizations/${organization?.id}`, value)
      .pipe(
        tap(() => {
          this.refreshOrganizations();
        })
      );
  }

  updateOrganizationBillingInfo(body: Partial<BillingInfoDto>) {
    const organization = this.organizations()?.[0];
    return this.authHttpClient
      .patch<BillingInfoDto>(`${environment.apiBaseUrl}/organizations/${organization?.id}/address`, body)
      .pipe(
        tap(() => {
          this.refreshOrganizations();
        })
      );
  }

  async updateUserScopes(id: string, newScopes: Scopes[]) {
    return firstValueFrom(this.authHttpClient.patch(`${environment.apiBaseUrl}/users/${id}/scopes`, newScopes));
  }

  async getOrgVolFromDune(orgId: string) {
    return firstValueFrom(
      this.authHttpClient.get<GetDuneVolDto[]>(`${environment.apiBaseUrl}/organizations/${orgId}/vol`)
    );
  }
}
